import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { AdminUser, AccountFormData, PERMISSIONS, ROLE_PERMISSIONS } from '../types';
import { getAccounts as fetchAccounts, createAccount as createAccountApi, updateAccount as updateAccountApi, deleteAccount as deleteAccountApi } from '../utils/api';
import { useLocation } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';

const AccountManagement: React.FC = () => {
  const location = useLocation();
  const [accounts, setAccounts] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<AdminUser | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<AccountFormData>({
    username: '',
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'operator',
    permissions: [],
    is_active: true,
  });
  
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    loadAccounts();
    // ルートやstateで新規作成を開く
    try {
      const shouldOpen = (location.pathname.endsWith('/admin/users/new') || (location.state as any)?.openCreate);
      if (shouldOpen) {
        handleCreateAccount();
      }
    } catch {}
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const { accounts } = await fetchAccounts();
      setAccounts(accounts || []);
    } catch (err) {
      setError('アカウント一覧の取得中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = () => {
    setEditingAccount(null);
    setFormData({
      username: '',
      displayName: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'operator',
      permissions: [],
      is_active: true,
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleEditAccount = (account: AdminUser) => {
    setEditingAccount(account);
    setFormData({
      username: account.username,
      displayName: account.displayName,
      email: account.email,
      password: '',
      confirmPassword: '',
      role: account.role,
      permissions: [...(account.permissions || ROLE_PERMISSIONS[account.role] || [])],
      is_active: (account.isActive ?? account.is_active) !== false,
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleDeleteAccount = async (account: AdminUser) => {
    if (!window.confirm(`アカウント「${account.displayName}」を削除しますか？この操作は取り消せません。`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await deleteAccountApi(account.id);
      if (response.success) {
        setSuccess('アカウントを削除しました');
        await loadAccounts();
      } else {
        setError(response.message || 'アカウントの削除に失敗しました');
      }
    } catch (err) {
      setError('アカウントの削除中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.username.trim()) {
      errors.username = 'ユーザー名は必須です';
    }

    if (!formData.displayName.trim()) {
      errors.displayName = '表示名は必須です';
    } else if (!/[\s\u3000]/.test(formData.displayName.trim())) {
      errors.displayName = '姓と名の間にスペースを入れてください（例: 山田 太郎）';
    }

    if (!formData.email.trim()) {
      errors.email = 'メールアドレスは必須です';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = '有効なメールアドレスを入力してください';
    }

    if (!editingAccount) {
      if (!formData.password) {
        errors.password = 'パスワードは必須です';
      } else if (formData.password.length < 6) {
        errors.password = 'パスワードは6文字以上で入力してください';
      }
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'パスワードが一致しません';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData: any = { ...formData };
      if (submitData.confirmPassword) {
        delete submitData.confirmPassword;
      }

      if (editingAccount) {
        // 編集時はパスワードが空の場合は除外
        if (!submitData.password) {
          delete submitData.password;
        }
        const response = await updateAccountApi(editingAccount.id, submitData);
        if (response.success) {
          setSuccess('アカウントを更新しました');
        } else {
          setError(response.message || 'アカウントの更新に失敗しました');
          return;
        }
      } else {
        const response = await createAccountApi(submitData);
        if (response.success) {
          setSuccess('アカウントを作成しました');
        } else {
          setError(response.message || 'アカウントの作成に失敗しました');
          return;
        }
      }

      setOpenDialog(false);
      await loadAccounts();
    } catch (err) {
      setError('アカウント処理中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (role: 'admin' | 'operator') => {
    setFormData(prev => ({
      ...prev,
      role,
      permissions: [...(ROLE_PERMISSIONS[role] || [])],
    }));
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return '管理者';
      case 'operator': return '運営者';
      default: return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'operator': return 'primary';
      default: return 'default';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'default';
  };

  return (
    <AdminLayout>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            アカウント管理
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateAccount}
          >
            新規アカウント作成
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ユーザー名</TableCell>
                    <TableCell>表示名</TableCell>
                    <TableCell>メールアドレス</TableCell>
                    <TableCell>役割</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell>最終ログイン</TableCell>
                    <TableCell>アクション</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && accounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : accounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        アカウントがありません
                      </TableCell>
                    </TableRow>
                  ) : (
                    accounts.map((account) => (
                      <TableRow key={account.id} hover>
                        <TableCell>{account.username}</TableCell>
                        <TableCell>{account.displayName}</TableCell>
                        <TableCell>{account.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={getRoleLabel(account.role)}
                            color={getRoleColor(account.role) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(account.isActive ?? account.is_active) !== false ? 'アクティブ' : '無効'}
                            color={getStatusColor((account.isActive ?? account.is_active) !== false)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {(account.lastLoginAt || account.last_login) ? new Date((account.lastLoginAt || account.last_login)!).toLocaleString() : '未ログイン'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditAccount(account)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteAccount(account)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* アカウント作成・編集ダイアログ */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingAccount ? 'アカウント編集' : '新規アカウント作成'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="ユーザー名"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  error={!!formErrors.username}
                  helperText={formErrors.username}
                  disabled={!!editingAccount}
                />
                <TextField
                  fullWidth
                  label="表示名"
                  placeholder="例: 山田 太郎"
                  value={formData.displayName}
                  onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                  error={!!formErrors.displayName}
                  helperText={formErrors.displayName || '姓と名の間にスペースを入れてください（個人情報保護のため、お客様には苗字のみ表示されます）'}
                />
              </Box>
              
              <TextField
                fullWidth
                label="メールアドレス"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={editingAccount ? 'パスワード（変更する場合のみ）' : 'パスワード'}
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <ViewIcon />}
                      </IconButton>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="パスワード確認"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  error={!!formErrors.confirmPassword}
                  helperText={formErrors.confirmPassword}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl fullWidth>
                  <InputLabel>役割</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => handleRoleChange(e.target.value as 'admin' | 'operator')}
                    label="役割"
                  >
                    <MenuItem value="operator">運営者</MenuItem>
                    <MenuItem value="admin">管理者</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                  }
                  label="アクティブ"
                />
              </Box>
              
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  権限:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.permissions.map((permission) => (
                    <Chip
                      key={permission}
                      label={permission.replace('_', ' ')}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>
              キャンセル
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : (editingAccount ? '更新' : '作成')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default AccountManagement; 